/**
 * The ONE place bot/KB markdown gets rendered in the Support Agent UI.
 *
 * Why this exists: `MDEditor.Markdown` (@uiw/react-md-editor) runs
 * `rehype-raw`, so raw HTML inside a markdown string is parsed into real
 * DOM elements and executed. Bot note bodies are not trustworthy input —
 * they interpolate Zendesk display names and customer-editable profile
 * fields (first/last name, org, device, app version). A customer who sets
 * their Zendesk display name to `<img src=x onerror=...>` would otherwise
 * get script execution in a POM Master's authenticated session every time
 * that ticket's drawer is opened (stored XSS).
 *
 * PLUGIN ORDER (verified against @uiw/react-markdown-preview's source, which
 * builds: reservedMeta, rehypeRaw, retrieveMeta, ...defaults, rehypeRewrite,
 * CALLER PLUGINS, rehypePrismPlus):
 *   - `rehype-sanitize` lands after `rehype-raw`, which is what matters —
 *     raw HTML has become elements by then, so the sanitizer sees and strips
 *     them, including every `on*` handler (hast-util-sanitize drops any
 *     attribute not explicitly allowed).
 *   - It is NOT the final step: `rehypePrismPlus` runs after us. That is
 *     safe because Prism only adds syntax-highlight classes to code blocks
 *     it generates itself; it never re-introduces caller-controlled HTML.
 *   - This ordering is an assumption about library internals, not a type
 *     contract. If a future @uiw version moves a raw-HTML-producing plugin
 *     after caller plugins, sanitization would silently stop covering it.
 *     The payload assertions that guard this live outside Jest (CRA's
 *     transform can't load these ESM deps) — see the PR for the follow-up to
 *     make that an in-suite regression test.
 *
 * NOTE: `skipHtml` is NOT a substitute. By the time react-markdown would
 * honor it, `rehype-raw` has already converted raw nodes into elements.
 *
 * The service side also strips `<`/`>` when composing notes
 * (pomsafe-services composeNote.ts `escapeNoteField`); this is the second,
 * independent layer, and the one that protects content this UI did not
 * compose — recovered notes, KB docs, older records written before that fix.
 */
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

/**
 * Class names we actually need to survive sanitization: `language-*` (read
 * by rehypePrismPlus downstream to pick a grammar), heading-anchor classes
 * from rehype-autolink-headings, and the component's own `wmde-*`.
 *
 * Deliberately an allowlist, not `'className'` unrestricted: `rehype-attr`
 * runs BEFORE us and honours markdown-embedded attribute syntax (`{.cls}`),
 * which contains no angle brackets and therefore also survives the
 * compose-side `<>` stripping. Unrestricted classes are not script
 * execution, but they would let a crafted note style itself to spoof drawer
 * UI (fake banners, hidden text) for the admin reading it.
 */
const ALLOWED_CLASS =
  /^(?:language-|lang-|code-|line-|highlight-|token|hljs|anchor|wmde-|prism-|katex|math)/;

const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), ['className', ALLOWED_CLASS], 'id'],
  },
};

/**
 * `rehypePlugins` is intentionally NOT part of this component's props: a
 * caller passing their own would be concatenated ahead of nothing useful and
 * could prepend a plugin that re-introduces raw HTML after sanitization.
 * If a call site needs more (e.g. `remarkPlugins`, `components`), widen this
 * signature explicitly rather than bypassing SafeMarkdown for a bare
 * `MDEditor.Markdown` — bypassing is what re-opens the XSS.
 */
export function SafeMarkdown({ source }: { source?: string }) {
  return <MDEditor.Markdown source={source} rehypePlugins={[[rehypeSanitize, schema]]} />;
}
